import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  History,
  Plus,
  Sparkles,
  Bug,
  Wrench,
  AlertTriangle,
  Rocket,
  Shield,
  Tag,
  Calendar,
  User,
  ChevronDown,
  ChevronUp,
  Save,
  Zap,
  Database,
  Layout,
  Users,
  CreditCard,
  Gift,
  Settings,
  FileText,
  Gamepad2,
  Server,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ChangelogEntry {
  id: string;
  version: string;
  date: string;
  author: string;
  type: "major" | "minor" | "patch" | "hotfix";
  changes: {
    type: "feature" | "improvement" | "bugfix" | "security" | "breaking";
    module: string;
    description: string;
  }[];
}

const mockChangelog: ChangelogEntry[] = [
  {
    id: "2.16.1",
    version: "2.16.1",
    date: "2026-02-16",
    author: "KS",
    type: "patch",
    changes: [
      {
        type: "improvement",
        module: "Slips",
        description:
          "Slip Dialog Display — เปลี่ยนการแสดงรูปสลิปจากการเปิดหน้าใหม่เป็นการแสดงใน dialog โดยตรง ทั้งในหน้ารายการสลิปและหน้าธุรกรรมฝาก-ถอน พร้อมปุ่มเปิดในหน้าใหม่สำหรับผู้ที่ต้องการดูแบบเต็มหน้าจอ ปรับปรุง UX ให้สะดวกและรวดเร็วขึ้น",
      },
    ],
  },
  {
    id: "2.16.0",
    version: "2.16.0",
    date: "2026-02-15",
    author: "KS",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "ACL System",
        description:
          "Granular Permission Management — ระบบจัดการสิทธิ์แบบละเอียด 7 roles (Super Admin, Operations Manager, Cashier, Support, Marketing, Partner Manager, Partner) พร้อม action-level permissions (view, create, edit, delete, approve, reject, export) ครอบคลุม 20 เมนู",
      },
      {
        type: "improvement",
        module: "Documentation",
        description:
          "ACL Analysis & Planning — วิเคราะห์ระบบสิทธิ์เดิม (Olive 3-tier: admin_group 0,1,3), สร้างแผน migration, permission matrix ครบถ้วน, implementation plan 4 phases พร้อม use cases แต่ละ role",
      },
      {
        type: "improvement",
        module: "Roles",
        description:
          "Support Role (admin_group 2) — Customer support team สามารถดูและแก้ไขข้อมูลสมาชิก, ดูประวัติธุรกรรม, ตรวจสอบอุปกรณ์, ตอบคำถามเกี่ยวกับโปรโมชั่น (ไม่สามารถอนุมัติ/ปฏิเสธธุรกรรม)",
      },
      {
        type: "improvement",
        module: "Roles",
        description:
          "Partner Manager Role — จัดการระบบ Partner/Affiliate ทั้งหมด: อนุมัติ partners ใหม่, กำหนดอัตรา commission, อนุมัติการจ่ายเงิน, ดู analytics ของ partners ทั้งหมด, จัดการ affiliate links",
      },
    ],
  },
  {
    id: "2.15.0",
    version: "2.15.0",
    date: "2026-02-13",
    author: "KS",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Smart Cashier",
        description:
          "Smart Slip Upload — อัปโหลดสลิปอัตโนมัติพร้อม auto member matching (95% phone, 90% bank account), duplicate detection (SlipLog, LineConnectLog, DepositWithdraw), web bank display based on bank_type, Slip2Go API integration",
      },
      {
        type: "feature",
        module: "Transactions",
        description:
          "Batch Reject API (Cancel All) — ปฏิเสธธุรกรรมพร้อมกันได้ครั้งละหลายรายการ (parallel processing 10/batch) รองรับทั้ง Deposit และ Withdraw, performance target: 10tx<5s, 50tx<15s, 100tx<30s",
      },
      {
        type: "improvement",
        module: "Testing",
        description:
          "Backend Test Suite — เพิ่มการทดสอบครบถ้วน 423 tests (smart-slip-upload 18 tests, batch-reject 23 tests) รองรับ auto admin user creation, proper cleanup, performance testing",
      },
      {
        type: "improvement",
        module: "Testing",
        description:
          "Frontend E2E Test Suite — เพิ่มการทดสอบ UI ครบถ้วน ~367 tests (smart-slip-upload 37 tests: page layout, file upload, success/error handling, duplicate detection, responsive design, accessibility)",
      },
      {
        type: "improvement",
        module: "Documentation",
        description:
          "Test Execution Plan — สร้างแผนการทดสอบครบถ้วนทั้ง Backend และ Frontend พร้อม TEST_READY.md, TESTING.md trackers, execution guides",
      },
    ],
  },
  {
    id: "2.14.0",
    version: "2.14.0",
    date: "2026-02-10",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Migration",
        description:
          "เพิ่ม migrate-v2.js — สคริปต์อัพเกรด V1 → V2 ผ่าน Parse REST API (สร้าง 13 classes, 30 DW fields, 5 User fields, 2 indexes, 17 CronJobs, feature flags) รองรับ --dry-run และ idempotent",
      },
      {
        type: "feature",
        module: "Migration",
        description:
          "เพิ่ม migrate-full.js — pipeline รวม DB Clone + V2 Upgrade ในขั้นตอนเดียว รองรับ --skip-phase1, --skip-phase2",
      },
      {
        type: "feature",
        module: "System",
        description:
          "เพิ่ม MCP Read-Only mode — ป้องกันการเขียนข้อมูลผ่าน MCP สำหรับ external servers (aurora) + ตั้งชื่อ server ด้วย MCP_SERVER_NAME",
      },
      {
        type: "feature",
        module: "System",
        description:
          "Aurora1688 schema migration สำเร็จ — เพิ่มโครงสร้าง V2 ครบถ้วน (74 classes เท่า olive, indexes ตรงกัน, CronJobs 17 ตัว, feature flags เปิดใช้)",
      },
      {
        type: "improvement",
        module: "Documentation",
        description:
          "อัปเดต DATABASE_MIGRATION.md — เพิ่ม overview table, decision tree, full pipeline diagram, Quick Start 3 options",
      },
    ],
  },
  {
    id: "2.13.0",
    version: "2.13.0",
    date: "2026-02-09",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "System",
        description:
          "เพิ่ม System Health Audit Phase 1 (Withdraw) — ตรวจสอบ stuck transactions, gateway timeout, stuck suspend พร้อมส่ง Telegram แจ้งเตือนอัตโนมัติ",
      },
      {
        type: "feature",
        module: "System",
        description:
          "เพิ่ม Health Audit MCP Server — เรียก audit_run, audit_check, audit_send_telegram ผ่าน MCP ได้",
      },
      {
        type: "feature",
        module: "Transactions",
        description:
          "เพิ่มระบบตรวจจับ Deposit ซ้ำ (±60s window) — ป้องกันการฝากซ้ำจากยอดเดียวกันในเวลาใกล้เคียง พร้อม test suite ครบ",
      },
      {
        type: "bugfix",
        module: "Transactions",
        description:
          "แก้ไข checkPending ตรวจสอบทั้ง Pending และ Processing (เดิมตรวจแค่ Pending — ทำให้ถอนซ้ำได้ขณะ Processing)",
      },
      {
        type: "bugfix",
        module: "Transactions",
        description:
          "แก้ไข Deposit addCredit คืน status ถูกต้องเมื่อล้มเหลว (เดิมคืน success แม้ addCredit fail)",
      },
      {
        type: "bugfix",
        module: "Backend",
        description:
          "แก้ไข apiController ขาด return ก่อน res.json() — ทำให้เกิด double response, เพิ่มรองรับ withdraw codes 1000/1001/1002",
      },
    ],
  },
  {
    id: "2.12.0",
    version: "2.12.0",
    date: "2026-02-08",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "improvement",
        module: "Backend",
        description:
          "WinLose เปลี่ยนเป็น MemberActivity V2 เป็น default — แก้ปัญหา LastPlay จำกัด 90k members, ใช้ batch pagination (1000/batch) แทน",
      },
      {
        type: "feature",
        module: "Backend",
        description:
          "เพิ่ม Deposit/Withdraw integration test suites ครบถ้วน — ทดสอบ flow ฝาก-ถอนทุก scenario",
      },
      {
        type: "bugfix",
        module: "Games",
        description:
          "แก้ไข Game Sync — Provider image/logoMobileURL หาย และ GameList provider_id ไม่เป็น Pointer",
      },
      {
        type: "improvement",
        module: "System",
        description:
          "ปรับโครงสร้าง env config สำหรับ mandarin168.com + เพิ่ม patch-config.sh สำหรับ multi-brand deployment",
      },
      {
        type: "improvement",
        module: "Documentation",
        description:
          "อัปเดตผลทดสอบ — 772 backend tests + 352 frontend E2E tests ผ่านทั้งหมด",
      },
    ],
  },
  {
    id: "2.11.0",
    version: "2.11.0",
    date: "2026-02-04",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Partner Portal",
        description:
          "เชื่อมต่อ Partner Portal กับ Backend API — Login, Dashboard, รายการสมาชิก, รายงาน ใช้ข้อมูลจริงจาก Parse Server",
      },
      {
        type: "feature",
        module: "Partner Portal",
        description:
          "เพิ่ม Partner Leader (admin_group = 4) — หัวหน้าทีมสามารถดูภาพรวมทีม, จัดการสมาชิกในทีม, และรายงานทีม",
      },
      {
        type: "feature",
        module: "Partner Portal",
        description:
          "เพิ่มหน้าภาพรวมทีม (Team Overview) — แสดง Partners ในทีม, สมาชิกรวม, คอมมิชชั่น, และ Top performers",
      },
      {
        type: "feature",
        module: "Partner Portal",
        description:
          "เพิ่มหน้าจัดการทีม (Team Management) — เพิ่ม/ลบ Partner เข้าทีม พร้อมข้อมูลติดต่อ",
      },
      {
        type: "feature",
        module: "Partner Portal",
        description:
          "เพิ่มหน้ารายงานทีม (Team Reports) — คอมมิชชั่นรวมของทีม แยกตาม Partner พร้อมกรองวันที่",
      },
      {
        type: "improvement",
        module: "Partner Portal",
        description:
          "เพิ่ม Calendar Date Picker — เลือกวันที่ด้วย Popup Calendar แทน Input type=date",
      },
      {
        type: "improvement",
        module: "Partner Portal",
        description:
          "เพิ่ม DEV Badge ที่เมนู Sidebar — แสดงสถานะหน้าที่ยัง mock data, ซ่อนตอน production build",
      },
      {
        type: "bugfix",
        module: "Partner Portal",
        description:
          "แก้ไขปัญหา ฿NaN ในหน้ารายงานและสมาชิก — เพิ่ม Number() conversion ก่อน formatCurrency",
      },
      {
        type: "improvement",
        module: "Backend",
        description:
          "เพิ่ม Cloud Functions สำหรับ Partner Portal — getPartnerDashboard, getPartnerReferrals, getPartnerReport",
      },
      {
        type: "improvement",
        module: "Backend",
        description:
          "รองรับ Partner Leader (admin_group = 4) ใน validatePartner — ใช้งานได้ทั้ง Partner และ Leader",
      },
    ],
  },
  {
    id: "2.10.0",
    version: "2.10.0",
    date: "2026-02-03",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Transactions",
        description:
          "ปรับปรุงระบบเก็บรูป Slip — เปลี่ยน path เป็น public/uploads/slips/ เพื่อให้เรียกดูผ่าน URL ได้โดยตรง",
      },
      {
        type: "feature",
        module: "System",
        description:
          "เพิ่ม Cron Job ลบรูป Slip อัตโนมัติ — ลบรูปที่เก่ากว่า 7 วัน ทุกวันจันทร์ 03:00 น. เพื่อจัดการพื้นที่",
      },
      {
        type: "improvement",
        module: "Transactions",
        description:
          "เพิ่ม date prefix ใน slip filename: slip-YYYY-MM-DD-timestamp.jpg สำหรับจัดการไฟล์ง่ายขึ้น",
      },
    ],
  },
  {
    id: "2.9.0",
    version: "2.9.0",
    date: "2026-02-02",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Promotions",
        description:
          "เพิ่ม Phase 1 fields: limits (max_usage_total, max_usage_per_member, budget_limit), priority, is_exclusive, image_url สำหรับควบคุมโปรโมชันละเอียดขึ้น",
      },
      {
        type: "feature",
        module: "Promotions",
        description:
          "เพิ่ม Promotion Category (10 หมวดหมู่): new_member, first_deposit, daily_deposit, reload, cashback, lossback, referral, special, event, other",
      },
      {
        type: "feature",
        module: "Promotions",
        description:
          "เพิ่ม UI sections ใหม่ใน Promotion Dialog: กลุ่มเป้าหมายและลำดับความสำคัญ, จำกัดการใช้งาน",
      },
      {
        type: "improvement",
        module: "Promotions",
        description:
          "แก้ไข afterSave trigger รองรับทั้ง V1 (Approved) และ V2 (Completed) status — โปรโมชันทำงานกับ DepositWithdraw V2",
      },
      {
        type: "improvement",
        module: "Promotions",
        description:
          "แก้ไข isFirstDeposit() ใช้ containedIn(['Approved', 'Completed']) รองรับทั้ง V1/V2",
      },
      {
        type: "improvement",
        module: "Backend",
        description:
          "เพิ่ม deposit-service-v2.createFromPromotion() สำหรับสร้าง bonus deposit จากโปรโมชัน",
      },
      {
        type: "improvement",
        module: "Documentation",
        description:
          "เขียน Module Doc v2.0 (05-promotions.md) และ Technical Spec ใหม่ (PROMOTION-SYSTEM-SPEC.md) พร้อม diagrams",
      },
    ],
  },
  {
    id: "2.8.1",
    version: "2.8.1",
    date: "2026-01-31",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "improvement",
        module: "Cron Jobs",
        description:
          "Reset Perday: กำหนด schedule 00:00 (เที่ยงคืน), concurrent: false, เอกสารชัดเจน 00:00–00:03 ทำครั้งเดียวต่อ Member ต่อวัน",
      },
      {
        type: "improvement",
        module: "Cron Jobs",
        description:
          "ลบ cron ที่ deprecated (report/notification/cleanup/backup/sync_member_balance) ออกจาก createDefaultCronJobs — เก็บใน CRON_JOBS_REMOVED_IDEAS.md",
      },
      {
        type: "improvement",
        module: "Cron Jobs",
        description:
          "ปุ่มสร้างงานตัวอย่างเรียก Cloud Function createDefaultCronJobs (backend เป็น single source of truth)",
      },
      {
        type: "improvement",
        module: "Cron Jobs",
        description:
          "เพิ่ม Cloud Function removeDeprecatedCronJobs — ลบ cron เก่าออกจาก DB ได้",
      },
      {
        type: "improvement",
        module: "UI Standards",
        description:
          "มาตรฐานแจ้งเตือน: ใช้ AlertDialog/ConfirmDialog สำหรับยืนยัน ใช้ toast (sonner) สำหรับผลลัพธ์ — ห้ามใช้ alert/confirm",
      },
      {
        type: "improvement",
        module: "Cron Jobs Page",
        description:
          "แทนที่ alert() ทั้งหมดด้วย toast.success / toast.error ในหน้า Cron Jobs",
      },
      {
        type: "improvement",
        module: "Work Tracking",
        description:
          "เพิ่ม AI_SESSION_LOG.md และกฎ session-handoff — อัปเดตหลังทำงานเพื่อให้เห็นความคืบหน้า",
      },
      {
        type: "improvement",
        module: "Changelog",
        description:
          "อัปเดต Changelog หน้า Admin — version 2.8.1, วันที่และรายละเอียดถูกต้อง พร้อมกฎ Cursor ให้อัปเดตเมื่อมีการปรับ UI/ระบบและผ่านการทดสอบ",
      },
    ],
  },
  {
    id: "1",
    version: "2.8.0",
    date: "2026-01-27",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Commission V2",
        description:
          "ระบบคอมมิชชั่น V2 - รองรับเลือกกลุ่มเกม, company_percent สำหรับ Partner, ถอนได้หลังเวลาที่กำหนด",
      },
      {
        type: "feature",
        module: "Commission V2",
        description:
          "ถอน Commission บางส่วนได้ - ไม่ต้องถอนทั้งหมด พร้อมประวัติการถอนและยอดสะสม",
      },
      {
        type: "feature",
        module: "Commission V2",
        description:
          "เพิ่ม MemberCommissionV2, CommissionCalculationLog, CommissionWithdrawV2 tables สำหรับเก็บข้อมูลแยกจาก V1",
      },
      {
        type: "feature",
        module: "Members",
        description:
          "ปรับปรุง Tab แนะนำเพื่อนใน Member Dialog - แสดงยอดคงเหลือ, ยอดที่ได้, ยอดถอน, รายชื่อแนะนำ L1/L2",
      },
      {
        type: "improvement",
        module: "Cron Jobs",
        description:
          "ปิด Commission V1 cron jobs (getMemberReferL1, getMemberReferL2, updateMemberReferTemp) - migrate ไป V2",
      },
      {
        type: "improvement",
        module: "Cron Jobs",
        description:
          "เปิด Commission V2 cron jobs - calculateMemberCommissionV2 (03:30), calculatePartnerCommissionV2 (04:00)",
      },
      {
        type: "improvement",
        module: "Settings UI",
        description:
          "ปรับปรุงหน้าตั้งค่า Commission - แยก Member/Partner, เลือกกลุ่มเกม, เพิ่ม withdraw_available_from",
      },
      {
        type: "improvement",
        module: "Backend Tests",
        description:
          "เพิ่ม 30 test cases สำหรับ Commission V2: Cloud Functions, calculation, withdrawal, time restriction - ผ่านทั้งหมด",
      },
    ],
  },
  {
    id: "2",
    version: "2.7.1",
    date: "2026-01-29",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Telegram",
        description:
          "เพิ่ม sendWinLoseSummary และ sendDailyResetSummary - แจ้งเตือนผลการคำนวณยอดเสีย/reset รายวัน พร้อม warning เมื่อไม่มีข้อมูล",
      },
      {
        type: "feature",
        module: "Telegram",
        description:
          "เพิ่มระบบ Notification Events - จัดหมวดหมู่ (Financial, Member, Security, System) พร้อม toggle เปิด/ปิด",
      },
      {
        type: "improvement",
        module: "Settings UI",
        description:
          "ปรับปรุง Multi-Channel Telegram UI - เพิ่มปุ่มทดสอบแต่ละ channel, ปุ่มลบ, toggle enabled/disabled",
      },
      {
        type: "improvement",
        module: "Backend",
        description:
          "สร้าง parse-instance.js utility - แก้ปัญหา 'Parse is not defined' ใน Jest tests สำหรับ 30 helper files",
      },
      {
        type: "improvement",
        module: "Cron Jobs",
        description:
          "เพิ่ม Telegram notifications ใน winlose-handlers.js - รายงานผลสำเร็จ, warning 0 members, และ error",
      },
      {
        type: "improvement",
        module: "Backend Tests",
        description:
          "เพิ่ม 35 test cases สำหรับ telegram-helper.js: config, sendMessage, channels, reports - ผ่านทั้งหมด",
      },
      {
        type: "improvement",
        module: "Backend Tests",
        description:
          "เพิ่ม 15 test cases สำหรับ winlose notifications: success, warning, error scenarios - ผ่านทั้งหมด",
      },
      {
        type: "improvement",
        module: "Frontend Tests",
        description:
          "เพิ่ม 25 E2E tests สำหรับ Telegram Settings UI: channels, events, responsive - ผ่านทั้งหมด",
      },
      {
        type: "bugfix",
        module: "Settings UI",
        description:
          "ปิด SMS section เป็น default (Coming Soon) - ป้องกันความสับสนเนื่องจากยังไม่พร้อมใช้งาน",
      },
    ],
  },
  {
    id: "2",
    version: "2.7.0",
    date: "2026-01-28",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Game Sync",
        description:
          "เพิ่มการแจ้งเตือน Telegram สำหรับ Game Sync - รายงานผลการ sync, เกมใหม่, และเกมที่ถูกลบ",
      },
      {
        type: "feature",
        module: "Telegram",
        description:
          "สร้างระบบ Multi-Channel Telegram Notifications - รองรับหลาย channel (Admin, Finance, Test)",
      },
      {
        type: "feature",
        module: "Telegram",
        description:
          "เพิ่ม Cloud Functions: setupTelegramConfig, getTelegramConfig, sendTestNotification สำหรับจัดการ Telegram",
      },
      {
        type: "improvement",
        module: "Cron Jobs",
        description:
          "เปลี่ยน sync_games cron job จากรายวัน เป็นรายเดือน (วันที่ 1 ของทุกเดือน) - ลดภาระ API",
      },
      {
        type: "bugfix",
        module: "Backend",
        description:
          "แก้ไข telegram-helper.js - ปรับการเข้าถึง telegramConfig จาก AppConfig Parse Object ให้ถูกต้อง",
      },
      {
        type: "improvement",
        module: "Backend Tests",
        description:
          "เพิ่ม 33 test cases สำหรับ WinLose Sync V2: feature flags, MemberActivity queries, daily stats, handlers - ผ่านทั้งหมด",
      },
      {
        type: "improvement",
        module: "Backend Tests",
        description:
          "เพิ่ม 25 test cases สำหรับ withdrawV3: daily limits, MemberActivity tracking, feature flag integration - ผ่านทั้งหมด",
      },
    ],
  },
  {
    id: "2",
    version: "2.6.0",
    date: "2026-01-28",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Bet History",
        description:
          "ระบบประวัติการเดิมพันใหม่ - ดึงข้อมูลจาก AMB API getBetHistoryByDepositID เก็บใน BetHistory class",
      },
      {
        type: "feature",
        module: "Bet History",
        description:
          "เพิ่ม Tab 'เกม' ใน Dialog จัดการสมาชิก - แสดงสรุปยอด, สถิติตามค่ายเกม, ตารางรายการเดิมพัน",
      },
      {
        type: "feature",
        module: "Bet History",
        description:
          "เพิ่ม Cron job syncDailyBetHistory - sync ประวัติเดิมพันรายวัน รองรับ batch processing",
      },
      {
        type: "feature",
        module: "Bet History",
        description:
          "เพิ่มปุ่ม Refresh สำหรับดึงข้อมูลล่าสุดจาก API แบบ on-demand",
      },
      {
        type: "improvement",
        module: "Agent API",
        description:
          "เปิดใช้งาน getBetHistoryByDepositID และ getWinLoseMultiDepositID ใน agent.js",
      },
      {
        type: "improvement",
        module: "Indexes",
        description:
          "สร้าง MongoDB indexes สำหรับ BetHistory: member_id, amb_ref_id, provider, bet_time",
      },
    ],
  },
  {
    id: "2",
    version: "2.5.9",
    date: "2026-01-28",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "MemberActivity V2",
        description:
          "สร้างระบบ MemberActivity V2 - tracking activity รายวันแยกจาก deposit_withdraw_perday_obj รองรับ 1000+ members/day",
      },
      {
        type: "feature",
        module: "MemberActivity V2",
        description:
          "เพิ่ม canWithdrawV2() และ canDepositV2() - ดึง config จาก AppConfig อัตโนมัติ เทียบค่าเหมือนระบบเดิม",
      },
      {
        type: "feature",
        module: "MemberActivity V2",
        description:
          "เพิ่ม withdrawV3() - รองรับ option use_member_activity เลือกใช้ MemberActivity หรือ legacy",
      },
      {
        type: "feature",
        module: "Cron Jobs V2",
        description:
          "ปรับ winlose-handlers.js รองรับทั้ง LastPlay และ MemberActivity พร้อม batch processing สำหรับ 1000+ members",
      },
      {
        type: "feature",
        module: "Cron Jobs V2",
        description:
          "เพิ่ม getAllLastPlayForWinLoseTurnOverV2() และ getAllLastPlayForResetPerdayV2() - optimized for high volume",
      },
      {
        type: "improvement",
        module: "MemberActivity V2",
        description:
          "เพิ่ม compareWithLegacy() สำหรับ debug เปรียบเทียบข้อมูลกับ deposit_withdraw_perday_obj",
      },
      {
        type: "improvement",
        module: "MemberActivity V2",
        description:
          "เพิ่ม getDailyStats() สำหรับ monitoring statistics รายวัน",
      },
      {
        type: "improvement",
        module: "Backend Tests",
        description:
          "เพิ่ม 19 test cases สำหรับ MemberActivity V2: canWithdrawV2, canDepositV2, compareWithLegacy, withdrawV3 - ผ่านทั้งหมด",
      },
      {
        type: "improvement",
        module: "E2E Tests",
        description:
          "เพิ่ม 45 test cases สำหรับ Member Activity UI: Desktop (Chromium, Firefox, WebKit) และ Mobile Chrome - ผ่านทั้งหมด",
      },
      {
        type: "improvement",
        module: "Indexes",
        description:
          "สร้าง MongoDB indexes สำหรับ MemberActivity: member_date_unique, date_has_play, date_has_deposit, date_has_withdraw",
      },
    ],
  },
  {
    id: "0",
    version: "2.5.8",
    date: "2026-01-28",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Agent V2",
        description:
          "สร้าง HTTP Client ใหม่พร้อม Connection Pooling - รองรับ 50 connections พร้อมกัน, Keep-Alive 30 วินาที, ลดการสร้าง TCP connection ซ้ำ",
      },
      {
        type: "feature",
        module: "Agent V2",
        description:
          "เพิ่ม Retry with Exponential Backoff - retry อัตโนมัติ 3 ครั้ง เมื่อ request ล้มเหลว พร้อม delay เพิ่มขึ้นทีละเท่าตัว (1s, 2s, 4s)",
      },
      {
        type: "feature",
        module: "Agent V2",
        description:
          "เพิ่ม Rate Limiting - จำกัด 50 requests/วินาที ป้องกัน AMB API ถูก block",
      },
      {
        type: "feature",
        module: "Agent V2",
        description:
          "เพิ่ม Circuit Breaker - หยุด request อัตโนมัติหลัง fail 5 ครั้งติดต่อกัน รอ 30 วินาทีก่อน retry",
      },
      {
        type: "feature",
        module: "Agent V2",
        description:
          "เพิ่ม Request Timeout - fast (10s), normal (30s), slow (60s), gameList (2 นาที) ป้องกัน request ค้าง",
      },
      {
        type: "feature",
        module: "Agent V2",
        description:
          "Implement ครบทุก 27 AMB API Endpoints - รวม changePassword, getBetHistory, gameDetail, dashboard, tournament และอื่นๆ",
      },
      {
        type: "improvement",
        module: "Backend",
        description:
          "agent.js อัพเกรดใช้ http-client ใหม่ - Backward compatible ไม่กระทบโค้ดเดิม, fallback กลับไป await-request ถ้ามีปัญหา",
      },
      {
        type: "improvement",
        module: "Performance",
        description:
          "ลด latency และเพิ่ม throughput - Connection pooling ช่วยลดเวลา handshake, รองรับ traffic สูงได้ดีขึ้น",
      },
    ],
  },
  {
    id: "1",
    version: "2.5.7",
    date: "2026-01-28",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Game Sync",
        description:
          "สร้างระบบ Sync เกมใหม่ - ดึงข้อมูลเกมและ Provider จาก API เก็บลง Database พร้อม Cron Job รันอัตโนมัติทุกวัน 06:00 น.",
      },
      {
        type: "feature",
        module: "Games",
        description:
          "เพิ่มปุ่ม Sync เกม ในหน้าจัดการเกม พร้อม Confirm Dialog สวยงาม และแสดงผลลัพธ์การ Sync",
      },
      {
        type: "feature",
        module: "Games",
        description:
          "หน้าผู้ให้บริการ (Providers) - ดึงข้อมูลจริงจาก Provider class พร้อมนับจำนวนเกมในแต่ละ Provider",
      },
      {
        type: "feature",
        module: "Games",
        description:
          "หน้าประเภทเกม (Categories) - Aggregate จาก GameList.category แสดงจำนวนเกมในแต่ละประเภท (SLOT, CASINO, FISH, etc.)",
      },
      {
        type: "feature",
        module: "Settings",
        description:
          "เพิ่มการตั้งค่า Telegram ในหน้า Settings - Bot Token, Chat ID, Enable/Disable พร้อมปุ่ม Test",
      },
      {
        type: "feature",
        module: "UI Components",
        description:
          "สร้าง ConfirmDialog component ใหม่ - รองรับ 5 variants (default, warning, danger, success, info) ใช้แทน window.confirm",
      },
      {
        type: "improvement",
        module: "Cron Jobs",
        description:
          "แก้ไข Run Now button ให้ทำงานได้ - เพิ่ม X-Parse-Application-Id header และ Confirm Dialog ก่อนรัน",
      },
      {
        type: "improvement",
        module: "Cron Jobs",
        description:
          "รองรับ handlerName field ใน CronJob class (นอกจาก handler) สำหรับ backward compatibility",
      },
      {
        type: "improvement",
        module: "Backend Tests",
        description:
          "เพิ่ม 19 test cases สำหรับ Game Sync: triggerGameSync, syncProviders, syncGameList, getGameSyncLogs, getGameStats, testTelegramNotification - รวม 381 tests ผ่านทั้งหมด",
      },
      {
        type: "improvement",
        module: "E2E Tests",
        description:
          "เพิ่ม 23 test cases สำหรับ Games และ Cron Jobs UI: Tabs, Sync button, Confirm dialog, Providers, Categories - รวม 259 tests ผ่านทั้งหมด",
      },
      {
        type: "bugfix",
        module: "Game Sync",
        description:
          "แก้ไข gameType.toUpperCase error - รองรับ gameType ที่เป็น array หรือ non-string",
      },
      {
        type: "bugfix",
        module: "API",
        description:
          "แก้ไข CronJobService fetch calls - ใช้ config.serverURL และเพิ่ม App ID header",
      },
    ],
  },
  {
    id: "1",
    version: "2.5.6",
    date: "2026-01-27",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "ACL",
        description:
          "ปรับปรุงระบบ ACL ให้ตรงกับกลุ่มแอดมิน 3 กลุ่ม: Super Admin (0), Admin (1), Partner (3) - ข้อมูลเก็บใน Database",
      },
      {
        type: "feature",
        module: "ACL",
        description:
          "สร้าง RolePermission records ครบ 3 กลุ่ม พร้อม permissions และ actions (view, create, edit, delete, export, approve)",
      },
      {
        type: "improvement",
        module: "Admins",
        description:
          "แก้ไข username ที่ผิดปกติ: 9000000→system_auto, null→admin2, 900000undefined→admin1",
      },
      {
        type: "improvement",
        module: "ACL",
        description:
          "แก้ไขหน้า ACL ให้แสดง 3 roles ตรงกับ database (ลบ Finance Manager, Operator, Marketing, Viewer ที่ไม่ใช้)",
      },
      {
        type: "bugfix",
        module: "Members",
        description:
          "แก้ไข parse-v2.adapter ให้ map field names ถูกต้อง (camelCase→snake_case) และรองรับ password update",
      },
      {
        type: "improvement",
        module: "Dashboard",
        description:
          "แก้ไขสีตัวเลขกำไร/ขาดทุนให้ถูกต้อง - สีเขียวเมื่อกำไร สีแดงเมื่อขาดทุน (กำไรสุทธิ, กระแสเงินสด)",
      },
      {
        type: "improvement",
        module: "Transactions",
        description:
          "แก้ไขสีกำไรสุทธิวันนี้ให้แสดงสีตามค่า - เขียวเมื่อบวก แดงเมื่อลบ พร้อมไอคอนและพื้นหลังที่เหมาะสม",
      },
      {
        type: "improvement",
        module: "Reports",
        description:
          "แก้ไขสีกำไรสุทธิในรายงานการเงินให้แสดงตามค่าจริง (Financial Report)",
      },
    ],
  },
  {
    id: "0",
    version: "2.5.5",
    date: "2026-01-27",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Lossback",
        description:
          "เพิ่มระบบแจ้งเตือนสมาชิกเมื่อคืนยอดเสียสำเร็จ - สร้าง Notification type 'lossback' แสดงในกล่องข้อความของ Member",
      },
      {
        type: "feature",
        module: "Lossback",
        description:
          "เพิ่ม notify_member toggle ในหน้า Auto Lossback Config และ Manual Lossback - เลือกแจ้งเตือนหรือไม่แจ้งเตือนสมาชิก",
      },
      {
        type: "feature",
        module: "Cron Jobs",
        description:
          "เพิ่ม Two-Step Lossback Process: calculate_daily_lossback (02:00) คำนวณยอด และ process_pending_lossback (06:00) โอนเงินพร้อมแจ้งเตือน",
      },
      {
        type: "feature",
        module: "Backend",
        description:
          "เพิ่ม notification-helper.js - Helper สำหรับสร้าง Notification รองรับ lossback และ type อื่นๆ ในอนาคต",
      },
      {
        type: "improvement",
        module: "Lossback",
        description:
          "แก้ไข setReturnToMember ให้สร้าง Notification หลังโอนเงินสำเร็จ เมื่อ notify_member=true",
      },
      {
        type: "improvement",
        module: "Backend Tests",
        description:
          "เพิ่ม 45 test cases: Auto Lossback (+12), Notification (+7), Return Lose (+6), MCP Server (+26) - รวม 611 tests ผ่านทั้งหมด",
      },
    ],
  },
  {
    id: "0",
    version: "2.5.4",
    date: "2026-01-26",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Lossback",
        description:
          "เพิ่มรอบ Semi-Monthly (รายครึ่งเดือน) - คืนยอดวันที่ 1-15 รันวันที่ 16 และ 16-สิ้นเดือน รันวันที่ 1",
      },
      {
        type: "feature",
        module: "Members",
        description:
          "เพิ่ม Tab แนะนำเพื่อน (Referral) ใน Member Dialog - แสดงจำนวนเพื่อนและรายได้จากการแนะนำ 2 ชั้น",
      },
      {
        type: "improvement",
        module: "Members",
        description:
          "เชื่อมต่อ Cloud Function getMemberRefer - ดึงข้อมูลแนะนำเพื่อนจริงจาก Backend",
      },
      {
        type: "improvement",
        module: "Lossback",
        description:
          "เพิ่ม handlers และ cron jobs สำหรับ processSemiMonthlyLossback และ processMonthlyLossback",
      },
    ],
  },
  {
    id: "0",
    version: "2.5.3",
    date: "2026-01-25",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Lossback",
        description:
          "เพิ่มหน้าคืนยอดเสียอัตโนมัติ (Auto Lossback) - ตั้งค่า Config สำหรับคืนยอดอัตโนมัติตามรอบ (รายวัน/รายสัปดาห์/รายเดือน)",
      },
      {
        type: "feature",
        module: "Lossback",
        description:
          "รองรับ Auto Approve - คืนยอดให้สมาชิกทันทีโดยไม่ต้อง review เมื่อเปิดใช้งาน",
      },
      {
        type: "feature",
        module: "Lossback",
        description:
          "เพิ่ม Preview/Test ก่อนรัน - ดูตัวอย่างจำนวนสมาชิกและยอดคืนก่อนรันจริง",
      },
      {
        type: "improvement",
        module: "Lossback",
        description:
          "เชื่อมต่อ Backend Cloud Functions: getLossbackConfigs, createDefaultLossbackConfigs, updateLossbackConfig, toggleLossbackConfig, testLossbackConfig, executeLossbackConfig",
      },
      {
        type: "improvement",
        module: "Sidebar",
        description:
          "เพิ่มเมนูใหม่ 'คืนยอดอัตโนมัติ' ภายใต้กลุ่มเมนู 'คืนยอดเสีย'",
      },
    ],
  },
  {
    id: "0",
    version: "2.5.2",
    date: "2026-01-24",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Lossback",
        description:
          "เพิ่ม V2 Logic สำหรับการคืนยอดเสีย - รองรับการเลือกหลายวันและหลายเกม โดย group by member และรวมยอดทุกวัน/ทุกเกม",
      },
      {
        type: "improvement",
        module: "Lossback",
        description:
          "V2: สร้าง 1 ReturnLoseLog ต่อ member (แทน 1 record ต่อวัน) - รวมยอดเสียทุกวันและทุกเกมที่เลือกเข้าด้วยกัน",
      },
      {
        type: "improvement",
        module: "Lossback",
        description:
          "เพิ่ม fields ใหม่ใน ReturnLoseLog: dates_included (วันที่รวม), wl_by_game (ยอดแยกตามเกม), calculation_version (v2)",
      },
      {
        type: "improvement",
        module: "Lossback",
        description:
          "คงเก็บ V1 function (getWinLoseTurnOverByGameAndDate) ไว้เป็น backup - สามารถสลับกลับได้หากต้องการ",
      },
    ],
  },
  {
    id: "0",
    version: "2.5.1",
    date: "2026-01-23",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "bugfix",
        module: "Parse V2 Adapter",
        description:
          "Fixed Parse V2 adapter to use Cloud Functions instead of REST API endpoints (/api/v2/admins, /api/v2/members) which don't exist in Backend V2",
      },
      {
        type: "improvement",
        module: "Admin Service",
        description:
          "Updated admin_group field mapping to ensure proper number type conversion - fixes comparison issues between string and number values",
      },
      {
        type: "improvement",
        module: "Affiliates",
        description:
          "Changed affiliates page to fetch real data from backend - filters admins with admin_group = PARTNER instead of using mock data",
      },
      {
        type: "improvement",
        module: "Backend V2 Tests",
        description:
          "Completed comprehensive backend test suite with 100% pass rate (269 tests) covering auth, transactions, users, system, and E2E flows",
      },
    ],
  },
  {
    id: "1",
    version: "2.5.0",
    date: "2026-01-22",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Authentication",
        description:
          "Implemented multi-device session management - new login automatically destroys all existing sessions for the same user (TC-ADMIN-AUTH-011)",
      },
      {
        type: "improvement",
        module: "Session Security",
        description:
          "Enhanced canLogin Cloud Function to destroy old sessions before new login, ensuring only one active session per user",
      },
      {
        type: "bugfix",
        module: "Profile Page",
        description:
          "Fixed horizontal scroll issue on mobile viewport in Profile page by adding overflow-x-hidden to container (TC-ADMIN-AUTH-009)",
      },
      {
        type: "improvement",
        module: "Auth Provider",
        description:
          "Fixed offline fallback bug in AuthProvider - now correctly distinguishes between network errors and session validation errors",
      },
      {
        type: "improvement",
        module: "E2E Tests",
        description:
          "Added comprehensive Admin UI Authentication tests (11 test cases) covering login, logout, session management, responsive design, and multi-device scenarios",
      },
      {
        type: "improvement",
        module: "Backend Logging",
        description:
          "Added detailed logging to loginInCloud and canLogin Cloud Functions for better debugging and monitoring",
      },
    ],
  },
  {
    id: "1",
    version: "2.4.9",
    date: "2026-01-19",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "bugfix",
        module: "CORS Configuration",
        description:
          "Fixed CORS headers in Backend V2 - added X-Fingerprint-Id, X-Device-Id, X-Device-Type and other custom headers to allowed headers list",
      },
      {
        type: "bugfix",
        module: "Production Build",
        description:
          "Fixed production API URL configuration - frontend now correctly calls api.olive888.net/api/v1 instead of using relative path that caused 404 errors",
      },
      {
        type: "improvement",
        module: "Environment Config",
        description:
          "Set absolute URL in .env.production (VITE_PARSE_V2_SERVER_URL=https://api.olive888.net/api/v1) to ensure production builds work correctly",
      },
      {
        type: "improvement",
        module: "PM2 Setup",
        description:
          "Added PM2 ecosystem config and startup script for Backend V2 - auto-restart on server reboot with Node.js 20.19.0 via nvm",
      },
    ],
  },
  {
    id: "1",
    version: "2.4.6",
    date: "2026-01-19",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Backend Configuration",
        description:
          "Changed default backend from V1 to V2 - system now uses Parse Server V2 by default while maintaining V1 compatibility",
      },
      {
        type: "improvement",
        module: "Parse Client",
        description:
          "Updated all parse-client functions to use getCurrentParseConfig() for dynamic backend selection - ensures all services connect to correct backend",
      },
      {
        type: "improvement",
        module: "Domain Services",
        description:
          "Updated app-config and audit-log services to use getCurrentParseConfig() for proper backend V2 connectivity",
      },
      {
        type: "improvement",
        module: "Backend Switching",
        description:
          "Enhanced backend switching capability - V1 code remains available and can be switched back via environment variable or runtime API",
      },
      {
        type: "improvement",
        module: "Configuration",
        description:
          "Updated environment variable defaults and documentation - VITE_API_BACKEND now defaults to parse-v2",
      },
    ],
  },
  {
    id: "1",
    version: "2.4.7",
    date: "2026-01-19",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Internationalization",
        description:
          "Added comprehensive multi-language support (Thai, English, Lao, Khmer) - all UI text now supports 4 languages with proper locale handling",
      },
      {
        type: "improvement",
        module: "Dashboard",
        description:
          "Replaced all hardcoded text with translation keys - Dashboard now fully supports 4 languages with proper date/number formatting",
      },
      {
        type: "improvement",
        module: "Members",
        description:
          "Complete i18n implementation for Members page - columns, status labels, actions, pagination, and date formatting now support 4 languages",
      },
      {
        type: "improvement",
        module: "Transactions",
        description:
          "Added translation support for Smart Cashier console - all UI text, status messages, and labels now support 4 languages",
      },
      {
        type: "improvement",
        module: "Language Files",
        description:
          "Enhanced language files structure - added comprehensive translation keys for members and transactions modules in all 4 languages",
      },
    ],
  },
  {
    id: "2",
    version: "2.4.5",
    date: "2026-01-17",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Backend V2 Integration",
        description:
          "Completed Backend V2 integration with Parse Server 9.x - system is operational but requires thorough testing",
      },
      {
        type: "improvement",
        module: "Parse V2 Adapter",
        description:
          "Fixed field mapping to use V1 field names (snake_case) since Backend V2 still uses V1 schema",
      },
      {
        type: "improvement",
        module: "Parse Client",
        description:
          "Enhanced parse-client to support dynamic backend switching (V1/V2) based on environment configuration",
      },
      {
        type: "bugfix",
        module: "Member Data Display",
        description:
          "Fixed member list data display - now uses Cloud Function getCustomers and proper field mapping",
      },
      {
        type: "bugfix",
        module: "Vite Proxy Configuration",
        description:
          "Fixed Vite proxy to properly route /api/v2 requests to Backend V2 (port 5002) with path rewriting",
      },
      {
        type: "improvement",
        module: "Error Handling",
        description:
          "Enhanced error handling in Parse V2 adapter with better content-type validation and logging",
      },
    ],
  },
  {
    id: "1",
    version: "2.4.4",
    date: "2026-01-17",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Backend Infrastructure",
        description:
          "Added Backend V2 support with Parse Server 9.x, MongoDB 8, and Node.js 20 compatibility",
      },
      {
        type: "feature",
        module: "Frontend Configuration",
        description:
          "Added support for switching between Parse Backend V1 and V2 via environment variables",
      },
      {
        type: "feature",
        module: "Vite Proxy Configuration",
        description:
          "Enhanced Vite proxy to support both V1 (port 5000) and V2 (port 5002) backends with automatic path rewriting",
      },
      {
        type: "improvement",
        module: "Backend V2 Documentation",
        description:
          "Added comprehensive deployment documentation: UPLOAD-FILES.md, UPDATE-FILES.md, DEPLOYMENT-GUIDE.md",
      },
      {
        type: "improvement",
        module: "Error Handling",
        description:
          "Enhanced Parse V2 adapter error handling with better content-type validation and logging",
      },
      {
        type: "bugfix",
        module: "Parse V2 Adapter",
        description:
          "Fixed getCurrentUser endpoint call to use proper Parse standard endpoint with proxy support",
      },
    ],
  },
  {
    id: "1",
    version: "2.4.3",
    date: "2026-01-14",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Database Tools",
        description:
          "Added MongoDB Utilities CLI Tools - check version, export/import database, and migrate to DigitalOcean Managed MongoDB",
      },
      {
        type: "feature",
        module: "Database Tools",
        description:
          "Added 4 MongoDB utility scripts: check-version.js (read-only), export-db.js (backup), import-db.js (restore), migrate-to-digitalocean.js (migration)",
      },
      {
        type: "feature",
        module: "Database Schema Management",
        description:
          "Enhanced Database Schema Manager with add-fields command - automatically add missing fields to collections with default values",
      },
      {
        type: "improvement",
        module: "Database Schema Management",
        description:
          "Added full migration workflow script (migrate-full.js) - combines compare, migrate, add-fields, and validate in one command",
      },
      {
        type: "improvement",
        module: "Database Schema Management",
        description:
          "Updated documentation with clear distinction between read-only commands (snapshot, compare, validate) and write commands (migrate, add-fields)",
      },
      {
        type: "improvement",
        module: "Database Tools",
        description:
          "Added comprehensive documentation: README.md, SUMMARY.md, SAFETY.md, TROUBLESHOOTING.md, MIGRATION_GUIDE.md, and QUICK_START.md",
      },
      {
        type: "improvement",
        module: "Error Handling",
        description:
          "Enhanced MongoDB connection error messages with helpful troubleshooting tips and connection string format examples",
      },
    ],
  },
  {
    id: "1",
    version: "2.4.2",
    date: "2026-01-13",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "bugfix",
        module: "Cron Jobs",
        description:
          "Fixed member_id handling in Winlose Handlers - changed from memberId.id || memberId to memberId because member_id in LastPlay is string (objectId) not pointer",
      },
      {
        type: "bugfix",
        module: "Cron Jobs",
        description:
          "Fixed executeJob return value - returns { status: 'skipped', ... } instead of undefined when job is already running",
      },
      {
        type: "bugfix",
        module: "AppConfig",
        description:
          "Added beforeFind hook for AppConfig collection - supports master key, admin user, and ACL check. Fixed 500 error when querying AppConfig from frontend",
      },
      {
        type: "improvement",
        module: "Error Handling",
        description:
          "Added null check in testCronJob and executeCronJob functions - prevents error when executeJob returns undefined or null",
      },
    ],
  },
  {
    id: "1",
    version: "2.4.1",
    date: "2026-01-10",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Notification Settings",
        description:
          "Changed from LINE Notify to Telegram - updated notification settings in Settings page with Telegram Bot Token and Chat ID configuration",
      },
      {
        type: "feature",
        module: "Lossback History",
        description:
          "Added Winlose data display in Lossback History page - dynamic columns for each game (WL and VL), multi-select game filter, and Winlose Object Details Dialog",
      },
      {
        type: "improvement",
        module: "Data Querying",
        description:
          "Removed keys parameter from Parse Server queries to ensure all nested winlose_obj.data is fetched - added debug logging for data structure inspection",
      },
    ],
  },
  {
    id: "2",
    version: "2.4.0",
    date: "2026-01-09",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Database Schema Management",
        description:
          "Added Database Schema Management CLI Tool - create snapshots, compare schemas, migrate databases, and validate schema integrity",
      },
      {
        type: "feature",
        module: "CLI Tools",
        description:
          "Added 4 CLI commands: snapshot (create baseline), compare (diff schemas), migrate (sync databases), validate (check schema)",
      },
      {
        type: "improvement",
        module: "Development Tools",
        description:
          "Database schema versioning and migration tools - supports Parse Server types, nested fields, indexes (unique, sparse, TTL), and dry-run mode",
      },
    ],
  },
  {
    id: "3",
    version: "2.3.18",
    date: "2026-01-09",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Route Protection",
        description:
          "Added PermissionProtectedRoute component - route guard that checks both authentication and permissions before allowing access",
      },
      {
        type: "security",
        module: "Access Control",
        description:
          "Updated all routes to use PermissionProtectedRoute - prevents direct URL access when user doesn't have permission",
      },
      {
        type: "improvement",
        module: "Security",
        description:
          "Enhanced security layer - even if menu is hidden, users cannot access pages via direct URL without proper permissions",
      },
    ],
  },
  {
    id: "4",
    version: "2.3.17",
    date: "2026-01-09",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Role Permissions",
        description:
          "Added Role Permissions Database Integration - store role permissions in database (Parse Class RolePermission) instead of localStorage",
      },
      {
        type: "feature",
        module: "Backend",
        description:
          "Added Cloud Functions for role permissions: getRolePermissions and updateRolePermissions - only Super Admin can update",
      },
      {
        type: "improvement",
        module: "Role Service",
        description:
          "Updated RoleService to call Cloud Functions instead of localStorage - permissions now sync across all admins",
      },
      {
        type: "improvement",
        module: "Permission Hook",
        description:
          "Enhanced usePermission hook to load role permissions from database with caching - permissions persist across sessions",
      },
    ],
  },
  {
    id: "5",
    version: "2.3.16",
    date: "2026-01-09",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Access Control",
        description:
          "Added Admin Permission System - ACL page for managing menu permissions per admin, with usePermission hook and automatic sidebar filtering",
      },
      {
        type: "improvement",
        module: "Permission System",
        description:
          "Added PermissionService for managing menu permissions - supports both permissions array and adminGroup fallback for backward compatibility",
      },
      {
        type: "improvement",
        module: "Permission Logic",
        description:
          "Fixed permission logic to handle empty permissions array - now falls back to adminGroup + config instead of blocking all menus",
      },
    ],
  },
  {
    id: "6",
    version: "2.3.15",
    date: "2026-01-09",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Lossback",
        description:
          "Added V2 support for Return Lose system - checkMemberBonus and depositCashToMember now support both V1 (Approved) and V2 (Completed) statuses",
      },
      {
        type: "improvement",
        module: "Lossback Menu",
        description:
          "Restructured Lossback menu into submenu: 'Manage Lossback' and 'Loss Records' for better navigation",
      },
      {
        type: "feature",
        module: "Lossback History",
        description:
          "Added new Lossback History page - displays daily loss records with filters, summary cards, and game group display",
      },
      {
        type: "improvement",
        module: "Lossback History",
        description:
          "Added game group display in all sections: daily cards, detail dialog, and member dialog with badge visualization",
      },
    ],
  },
  {
    id: "7",
    version: "2.3.14",
    date: "2026-01-08",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "bugfix",
        module: "Transaction Detail Dialog",
        description:
          "Fixed Transaction Detail Dialog status badge to show correct status - now supports all V2 statuses (Completed, Processing, Cancelled, Rejected, Pending)",
      },
      {
        type: "improvement",
        module: "Transaction Table",
        description:
          "Added 'Operator' column to Transaction Table in Member Detail Dialog - displays updatedBy (last editor) and createdBy (creator) for better visibility",
      },
      {
        type: "bugfix",
        module: "Monthly Financial Report",
        description:
          "Fixed Monthly Financial Report calculations to support V2 status - now includes Completed transactions in loadDailyTableData and loadChartData",
      },
      {
        type: "improvement",
        module: "Monthly Financial Report",
        description:
          "Updated report queries to include both Approved (V1) and Completed (V2) statuses - ensures all successful transactions are counted correctly",
      },
    ],
  },
  {
    id: "1",
    version: "2.3.13",
    date: "2026-01-07",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Member API",
        description:
          "Added new endpoint POST /api/v2/frontend-app/get-member-by-phone - retrieve member data by phone number (unique identifier)",
      },
      {
        type: "improvement",
        module: "API Organization",
        description:
          "Organized API v2 endpoints with comment sections by domain (Authentication, Member, Deposit, Withdraw, Bank, Content, Game, Promotion, Settings, Validation, Contact, Webhook/Receiver)",
      },
      {
        type: "improvement",
        module: "API Documentation",
        description:
          "Added API documentation for get-member-by-phone endpoint - includes request/response format, usage examples, and authentication guide",
      },
    ],
  },
  {
    id: "2",
    version: "2.3.12",
    date: "2026-01-07",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "bugfix",
        module: "Dashboard",
        description:
          "Fixed Dashboard financial calculations - now supports both V1 (Approved) and V2 (Completed) transaction statuses",
      },
      {
        type: "improvement",
        module: "Dashboard",
        description:
          "Updated Dashboard queries to include Completed status for V2 transactions - ensures all successful transactions are counted correctly",
      },
      {
        type: "improvement",
        module: "Dashboard",
        description:
          "Fixed getFinancialStats, getMemberStats, and cloud function getDashboardData to properly calculate deposits, withdrawals, and member statistics for V2 transactions",
      },
    ],
  },
  {
    id: "1",
    version: "2.3.12",
    date: "2026-01-06",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Admin Alert System",
        description:
          "Implemented Admin Alert system - separated from Member Notification for admin panel notifications (pending deposits, withdraws, new members)",
      },
      {
        type: "feature",
        module: "Admin Alert System",
        description:
          "Added mark as viewed functionality - notifications disappear when admin clicks on them, tracked per admin in AdminViewedNotification collection",
      },
      {
        type: "improvement",
        module: "Admin Alert System",
        description:
          "Created new domain admin-alert with types, service, and hook (use-admin-alert) - completely separated from Member Notification",
      },
      {
        type: "improvement",
        module: "Admin Alert System",
        description:
          "Updated Header and Sidebar components to use AdminAlert instead of Notification - clear separation of concerns",
      },
      {
        type: "improvement",
        module: "Admin Alert System",
        description:
          "Added AdminViewedNotification helper and cloud functions - supports multiple notification types (member, deposit, withdraw, promotion, etc.)",
      },
      {
        type: "bugfix",
        module: "Admin Alert System",
        description:
          "Fixed admin_id field type - changed from String to Pointer<_User> to match Parse schema requirements",
      },
      {
        type: "improvement",
        module: "Admin Alert System",
        description:
          "Added comprehensive debug logging for troubleshooting notification filtering and display issues",
      },
    ],
  },
  {
    id: "1",
    version: "2.3.11",
    date: "2026-01-06",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Member API",
        description:
          "เพิ่ม device tracking สำหรับ Member signup และ signin - เก็บข้อมูล fingerprint และ device info เพื่อติดตามอุปกรณ์",
      },
      {
        type: "improvement",
        module: "Member API",
        description:
          "API /frontend-app/signup และ /frontend-app/signin รองรับ deviceInfo (optional) - backward compatible",
      },
      {
        type: "improvement",
        module: "Member API",
        description:
          "เพิ่มการตรวจสอบ device blocking ใน signin - ถ้าอุปกรณ์ถูกบล็อกจะ return error 403",
      },
      {
        type: "improvement",
        module: "Member API",
        description:
          "เพิ่ม LoginHistory สำหรับ Member - บันทึกประวัติการ login พร้อม device info",
      },
      {
        type: "improvement",
        module: "Member API",
        description:
          "Response ของ /frontend-app/signin เพิ่ม deviceId เพื่อให้ frontend เก็บไว้ใช้ในครั้งถัดไป",
      },
    ],
  },
  {
    id: "1",
    version: "2.3.10",
    date: "2026-01-06",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "improvement",
        module: "Member Detail",
        description:
          "เพิ่ม prop compactLayout ใน TransactionTable เพื่อแยก layout สำหรับ Member Detail Dialog และ Smart Cashier Console",
      },
      {
        type: "improvement",
        module: "Member Detail",
        description:
          "ปรับปรุง column widths ใน tab ธุรกรรมของ Member Detail Dialog - จำนวนเงินและช่องทางเลื่อนมาทางซ้ายมากขึ้น เพื่อให้สถานะเห็นชัดเจนขึ้น",
      },
      {
        type: "improvement",
        module: "Member Detail",
        description:
          "เพิ่มพื้นที่ให้ column สถานะใน tab ธุรกรรมเพื่อป้องกันการล้นและแสดงผลชัดเจนขึ้น",
      },
    ],
  },
  {
    id: "1",
    version: "2.3.9",
    date: "2026-01-06",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "improvement",
        module: "Member Detail",
        description:
          "ลบส่วน Audit Logs ออกจาก tab ธุรกรรม - แยกให้แสดงเฉพาะใน tab 'กิจกรรม' เพื่อลดความซ้ำซ้อน",
      },
      {
        type: "improvement",
        module: "Member Detail",
        description:
          "ปรับปรุง layout ของ tab ธุรกรรม - แสดงเฉพาะ Transaction Table และ Pagination เพื่อความเรียบง่าย",
      },
    ],
  },
  {
    id: "1",
    version: "2.3.8",
    date: "2026-01-06",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "improvement",
        module: "Member Detail",
        description:
          "ปรับปรุง tab ธุรกรรมใน Member Detail Dialog - ซ่อน columns 'เลขที่' และ 'รายละเอียด' เพื่อให้ดูสะดวกขึ้น",
      },
      {
        type: "improvement",
        module: "Member Detail",
        description:
          "เปลี่ยน TransactionDialog เป็น view-only เมื่อเปิดจาก Member Detail Dialog (ไม่สามารถแก้ไขหรือบันทึกได้)",
      },
      {
        type: "improvement",
        module: "Member Detail",
        description:
          "ปรับปรุง UX/UI ของ tab ธุรกรรม - เรียบง่าย สะดวก และแสดงข้อมูลที่จำเป็นเท่านั้น",
      },
      {
        type: "bugfix",
        module: "Transaction",
        description:
          "แก้ไขปัญหา Invalid Date ใน TransactionTable - ปรับปรุง dateTime parsing ให้รองรับทั้ง Date object และ string format",
      },
      {
        type: "improvement",
        module: "Transaction",
        description:
          "เพิ่ม prop hideColumns ใน TransactionTable เพื่อให้สามารถซ่อน columns ที่ไม่ต้องการได้",
      },
    ],
  },
  {
    id: "1",
    version: "2.3.7",
    date: "2026-01-05",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "bugfix",
        module: "Audit Log",
        description:
          "แก้ไขปัญหา Audit Log ไม่แสดงข้อมูล Member (name/username) เมื่อสร้างรายการฝากจากหน้าอัจฉริยะ",
      },
      {
        type: "improvement",
        module: "Audit Log",
        description:
          "เพิ่ม memberUsername และ memberName ใน metadata ของ Audit Log สำหรับรายการฝากที่สร้างแบบ Manual",
      },
      {
        type: "improvement",
        module: "Audit Log",
        description:
          "ปรับปรุง description ใน Audit Log ให้แสดงข้อมูล Member ชัดเจนขึ้น (ชื่อและ username)",
      },
      {
        type: "improvement",
        module: "Smart Cashier",
        description:
          "ปรับปรุง Bank Feed ให้ดึงเฉพาะจาก SlipLog และ LineConnectLog (ลบ DepositWithdraw ออก)",
      },
      {
        type: "improvement",
        module: "Smart Cashier",
        description:
          "ปรับปรุง date filtering สำหรับ SlipLog และ LineConnectLog ให้ใช้ datetime และ date_time_format fields",
      },
      {
        type: "bugfix",
        module: "Smart Cashier",
        description:
          "แก้ไขปัญหา matched items ไม่แสดงถึงวันที่ปัจจุบันใน Bank Feed",
      },
    ],
  },
  {
    id: "1",
    version: "2.3.6",
    date: "2026-01-05",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "improvement",
        module: "Transactions",
        description:
          "ปรับปรุง approval flow ให้ Manual และ Auto ทำงานเหมือนกัน - เลือกธนาคารก่อน แล้วค่อยยืนยัน",
      },
      {
        type: "improvement",
        module: "Transactions",
        description:
          "ปรับปรุง approve_auto ให้เปิด ApproveBankDialog โดยตรง (ไม่แสดง confirm dialog ก่อน) เหมือน approve_manual",
      },
      {
        type: "improvement",
        module: "Settings",
        description:
          "ย้าย Withdraw Limit ไปอยู่หลัง Auto Approve Switch เพื่อให้เห็นความสัมพันธ์ชัดเจน",
      },
      {
        type: "improvement",
        module: "Settings",
        description:
          "แสดง Withdraw Limit เฉพาะเมื่อเปิด Auto Approve เพื่อลดความสับสน",
      },
      {
        type: "improvement",
        module: "Settings",
        description:
          "ปรับปรุง description ของ Auto Approve และ Withdraw Limit ให้ชัดเจนขึ้น",
      },
    ],
  },
  {
    id: "1",
    version: "2.3.5",
    date: "2026-01-04",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Smart Cashier",
        description:
          "เพิ่ม DepositWithdraw pending ใน Bank Feed - แสดงรายการ pending ทั้งหมด (รวมรายการเก่าที่ยัง pending)",
      },
      {
        type: "improvement",
        module: "Smart Cashier",
        description:
          "เพิ่ม date filter 7 วันล่าสุดสำหรับ SlipLog และ LineConnectLog เพื่อเพิ่มความเร็วในการโหลด",
      },
      {
        type: "improvement",
        module: "Smart Cashier",
        description:
          "Pending deposits แสดงทั้งหมด (ไม่กรองด้วย date) เพื่อไม่พลาดรายการสำคัญ",
      },
      {
        type: "improvement",
        module: "Smart Cashier",
        description:
          "ปรับปรุง Bank Feed ให้แสดงรายการตามลำดับเวลา (ล่าสุดก่อน) รวม SlipLog, LineConnectLog และ DepositWithdraw",
      },
      {
        type: "improvement",
        module: "Smart Cashier",
        description:
          'เพิ่ม badge "Pending" สำหรับ DepositWithdraw pending ใน Bank Feed',
      },
      {
        type: "bugfix",
        module: "Smart Cashier",
        description: "แก้ไขปัญหา pending deposits หายไปเมื่อเพิ่ม date filter",
      },
    ],
  },
  {
    id: "1",
    version: "2.3.4",
    date: "2026-01-04",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Smart Cashier",
        description:
          "เพิ่ม Live Bank Feed แสดงข้อมูลจาก SlipLog และ LineConnectLog แบบ real-time",
      },
      {
        type: "improvement",
        module: "Smart Cashier",
        description:
          "ปรับปรุงการตรวจสอบ matched status ใช้ transfer_id สำหรับ SlipLog และ used+status สำหรับ LineConnectLog",
      },
      {
        type: "bugfix",
        module: "Smart Cashier",
        description:
          "แก้ไข RangeError: Invalid time value ใน LineConnectLog date parsing",
      },
      {
        type: "feature",
        module: "Device Tracking",
        description:
          "เก็บข้อมูล fingerprint ทั้งหมดจาก FingerprintJS (components ทั้งหมด) ใน raw_fingerprint field",
      },
      {
        type: "feature",
        module: "Device Tracking",
        description:
          "เพิ่ม fields ใหม่ใน UserDevice: device_memory, hardware_concurrency, max_touch_points, pixel_depth",
      },
      {
        type: "improvement",
        module: "Device Tracking",
        description:
          "เก็บข้อมูลเพิ่มเติมจาก Browser APIs: screenWidth, screenHeight, languages, cookieEnabled, doNotTrack",
      },
      {
        type: "bugfix",
        module: "Auth",
        description: "เพิ่ม deviceId field ใน LoginResult interface",
      },
      {
        type: "bugfix",
        module: "Devices",
        description: "ลบ unused imports และ variables ใน devices-page.tsx",
      },
      {
        type: "bugfix",
        module: "Profile",
        description: "ลบ unused imports ใน profile-page.tsx",
      },
    ],
  },
  {
    id: "3",
    version: "2.3.3",
    date: "2026-01-04",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Settings",
        description:
          "ย้าย Auto Approve switch จาก System Settings ไปที่ Finance Settings (ส่วนการถอนเงิน)",
      },
      {
        type: "feature",
        module: "Settings",
        description:
          "เพิ่ม withdraw_limit field ใน Finance Settings UI สำหรับกำหนดขีดจำกัดการถอนเงินอัตโนมัติ",
      },
      {
        type: "improvement",
        module: "Settings",
        description:
          "ปรับปรุงการ parse และ save withdraw_limit รองรับ string จาก API และ default เป็น 60 บาท",
      },
      {
        type: "feature",
        module: "Banks",
        description: "เพิ่ม Payment Gateway option ใน data bank list ของ TH",
      },
      {
        type: "feature",
        module: "Transactions",
        description:
          "เพิ่ม ApproveBankDialog สำหรับเลือกบัญชีธนาคารถอนเงินพร้อม Gateway API badge",
      },
      {
        type: "improvement",
        module: "Transactions",
        description:
          "แสดงบัญชีลูกค้าและบัญชีเว็บ columns ใน transaction table พร้อม BankImage และ CopyButton",
      },
      {
        type: "bugfix",
        module: "Transactions",
        description:
          "แก้ไขปัญหาไม่มี withdrawal bank accounts แสดงใน admin panel และ dialogs แม้แต่ default accounts",
      },
      {
        type: "bugfix",
        module: "Transactions",
        description:
          "แก้ไข TypeScript errors: unused imports (Clock, BankLogo, transactionId)",
      },
    ],
  },
  {
    id: "3",
    version: "2.3.2",
    date: "2026-01-03",
    author: "System",
    type: "patch",
    changes: [
      {
        type: "feature",
        module: "Transactions",
        description:
          "เพิ่มระบบเลือกสมาชิกสำหรับรายการฝาก/ถอนที่ไม่สามารถระบุได้ - สามารถเลือกสมาชิกและอนุมัติได้ในหน้าเดียว",
      },
      {
        type: "feature",
        module: "Transactions",
        description:
          "Auto-approve และ Auto-complete สำหรับรายการฝาก/ถอนที่สร้างโดย Admin - ข้ามขั้นตอนการอนุมัติอัตโนมัติ",
      },
      {
        type: "improvement",
        module: "Transactions",
        description:
          "ปรับปรุงการแสดงผลเลขที่รายการ (transfer_id) แสดง 8 หลักสุดท้ายพร้อม tooltip",
      },
      {
        type: "improvement",
        module: "Transactions",
        description:
          "แก้ไขการแสดงผลวันที่/เวลาให้ถูกต้องสำหรับรายการที่สร้างจาก Panel",
      },
      {
        type: "bugfix",
        module: "Transactions",
        description:
          "แก้ไขปัญหา member_obj ไม่อัพเดทเมื่อเปลี่ยน member_id ในรายการที่ไม่สามารถระบุได้",
      },
      {
        type: "bugfix",
        module: "Transactions",
        description:
          'แก้ไขปัญหา status_note แสดง "รอการตรวจสอบ" แทน "สำเร็จ" สำหรับรายการที่สร้างโดย Admin',
      },
      {
        type: "bugfix",
        module: "UI",
        description:
          'แก้ไข React Hooks error "Rendered fewer hooks than expected" ใน AIAssistant component',
      },
      {
        type: "bugfix",
        module: "UI",
        description:
          "แก้ไข ReferenceError: mappedStatus is not defined ใน transaction service",
      },
      {
        type: "improvement",
        module: "Backend",
        description:
          "เพิ่ม ObjInfo.unidentifiedMember() helper function แทน hardcoded member ID",
      },
      {
        type: "improvement",
        module: "Backend",
        description:
          "ปรับปรุง beforeSave และ afterSave hooks เพื่อ sync member_obj อัตโนมัติ",
      },
    ],
  },
  {
    id: "4",
    version: "2.6.0",
    date: "2025-12-05",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Member Groups",
        description:
          "เพิ่มระบบกลุ่มสมาชิก (VIP Tier) พร้อม Cloud Functions รองรับข้อมูลหลักแสน",
      },
      {
        type: "feature",
        module: "Cron Jobs",
        description:
          "เพิ่มหน้าจัดการ Cron Jobs พร้อมงานตัวอย่าง 12 งาน รองรับ Grid/List view",
      },
      {
        type: "feature",
        module: "Lossback",
        description: "เพิ่มระบบคืนยอดเสีย เชื่อมต่อ Backend จริง",
      },
      {
        type: "feature",
        module: "Promotions",
        description:
          "เพิ่มระบบจัดการโปรโมชั่น รองรับหลายประเภท (ฝากเงิน, Minigame, คืนยอด)",
      },
      {
        type: "improvement",
        module: "Reports",
        description: "ปรับปรุงรายงานการเงินรายเดือน เชื่อมต่อข้อมูลจริง",
      },
      {
        type: "improvement",
        module: "Slip Log",
        description: "ปรับปรุงหน้ารายการสลิป แสดง Bank Logo ถูกต้อง",
      },
      {
        type: "improvement",
        module: "Banks",
        description: "เพิ่มการกรองธนาคารตามสกุลเงิน (THB, LAK, KHR)",
      },
      {
        type: "improvement",
        module: "UI",
        description: "เพิ่ม .cursorrules สำหรับ AI Assistant",
      },
    ],
  },
  {
    id: "5",
    version: "2.5.0",
    date: "2025-11-27",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Affiliate Portal",
        description:
          "เพิ่มระบบ Affiliate Portal ให้พันธมิตร Login เข้ามาใช้งานได้",
      },
      {
        type: "feature",
        module: "ACL",
        description: "เพิ่มระบบจัดการสิทธิ์การเข้าถึง (Access Control List)",
      },
      {
        type: "improvement",
        module: "Settings",
        description: "ปรับปรุงหน้าตั้งค่าระบบให้ครบถ้วน",
      },
      {
        type: "improvement",
        module: "UI",
        description: "เพิ่มระบบ Multi-Theme (Midnight, Ocean)",
      },
    ],
  },
  {
    id: "6",
    version: "2.4.0",
    date: "2025-11-25",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Games",
        description: "เพิ่มระบบจัดการเกม, ผู้ให้บริการ, และหมวดหมู่",
      },
      {
        type: "feature",
        module: "Reports",
        description: "เพิ่มรายงานการเงินรายเดือน",
      },
      {
        type: "improvement",
        module: "Affiliates",
        description: "เพิ่มรายงานพันธมิตร",
      },
      {
        type: "bugfix",
        module: "Members",
        description: "แก้ไขปัญหาการค้นหาสมาชิก",
      },
    ],
  },
  {
    id: "7",
    version: "2.3.0",
    date: "2025-11-20",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Slips",
        description: "เพิ่มหน้ารายการสลิปจาก EasySlip/Slip2Go",
      },
      {
        type: "feature",
        module: "Audit Log",
        description: "เพิ่มระบบ Audit Log",
      },
      {
        type: "feature",
        module: "AI Assistant",
        description: "เพิ่ม AI Admin Assistant",
      },
      { type: "security", module: "Auth", description: "เพิ่มระบบ 2FA" },
    ],
  },
  {
    id: "8",
    version: "2.2.0",
    date: "2025-11-15",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Banks",
        description: "ปรับปรุงหน้าจัดการบัญชีธนาคาร",
      },
      {
        type: "feature",
        module: "Reports",
        description: "เพิ่มรายงานการเงิน, สมาชิก, ธุรกรรม, ธนาคาร",
      },
      {
        type: "improvement",
        module: "Dashboard",
        description: "ปรับปรุง Dashboard ให้แสดงข้อมูลครบถ้วน",
      },
    ],
  },
  {
    id: "9",
    version: "2.1.0",
    date: "2025-11-10",
    author: "System",
    type: "minor",
    changes: [
      {
        type: "feature",
        module: "Affiliates",
        description: "เพิ่มระบบจัดการพันธมิตร",
      },
      {
        type: "feature",
        module: "Promotions",
        description: "เพิ่มระบบจัดการโปรโมชั่น",
      },
      {
        type: "improvement",
        module: "UI",
        description: "ปรับปรุง UI/UX ทั้งระบบ",
      },
    ],
  },
  {
    id: "10",
    version: "2.0.0",
    date: "2025-11-01",
    author: "System",
    type: "major",
    changes: [
      {
        type: "breaking",
        module: "Core",
        description: "Migration จาก Ant Design Pro เป็น Shadcn/ui + TanStack",
      },
      { type: "feature", module: "UI", description: "ระบบ Dark/Light Mode" },
      {
        type: "feature",
        module: "i18n",
        description: "รองรับหลายภาษา (TH, EN)",
      },
      {
        type: "improvement",
        module: "Performance",
        description: "ปรับปรุงประสิทธิภาพการโหลดหน้า",
      },
    ],
  },
];

const typeConfig: Record<
  ChangelogEntry["type"],
  { label: string; color: string }
> = {
  major: { label: "Major", color: "bg-purple-500 text-white" },
  minor: { label: "Minor", color: "bg-blue-500 text-white" },
  patch: { label: "Patch", color: "bg-emerald-500 text-white" },
  hotfix: { label: "Hotfix", color: "bg-red-500 text-white" },
};

const changeTypeConfig: Record<
  string,
  { label: string; icon: React.ReactNode; color: string }
> = {
  feature: {
    label: "ฟีเจอร์ใหม่",
    icon: <Sparkles className="h-3 w-3" />,
    color: "text-emerald-500",
  },
  improvement: {
    label: "ปรับปรุง",
    icon: <Wrench className="h-3 w-3" />,
    color: "text-blue-500",
  },
  bugfix: {
    label: "แก้ไขบัก",
    icon: <Bug className="h-3 w-3" />,
    color: "text-orange-500",
  },
  security: {
    label: "ความปลอดภัย",
    icon: <Shield className="h-3 w-3" />,
    color: "text-red-500",
  },
  breaking: {
    label: "Breaking Change",
    icon: <AlertTriangle className="h-3 w-3" />,
    color: "text-purple-500",
  },
};

const moduleIcons: Record<string, React.ReactNode> = {
  "Partner Portal": <Users className="h-4 w-4" />,
  "Affiliate Portal": <Users className="h-4 w-4" />,
  ACL: <Shield className="h-4 w-4" />,
  "Commission V2": <Gift className="h-4 w-4" />,
  "Cron Jobs": <Database className="h-4 w-4" />,
  "Cron Jobs V2": <Database className="h-4 w-4" />,
  "MemberActivity V2": <Users className="h-4 w-4" />,
  "Bet History": <Gamepad2 className="h-4 w-4" />,
  "Agent API": <Server className="h-4 w-4" />,
  Indexes: <Database className="h-4 w-4" />,
  Settings: <Settings className="h-4 w-4" />,
  Telegram: <Gift className="h-4 w-4" />,
  UI: <Layout className="h-4 w-4" />,
  Games: <Zap className="h-4 w-4" />,
  Reports: <FileText className="h-4 w-4" />,
  Affiliates: <Users className="h-4 w-4" />,
  Members: <Users className="h-4 w-4" />,
  Transactions: <CreditCard className="h-4 w-4" />,
  Slips: <FileText className="h-4 w-4" />,
  "Audit Log": <History className="h-4 w-4" />,
  "AI Assistant": <Sparkles className="h-4 w-4" />,
  Auth: <Shield className="h-4 w-4" />,
  Banks: <CreditCard className="h-4 w-4" />,
  Dashboard: <Layout className="h-4 w-4" />,
  Promotions: <Gift className="h-4 w-4" />,
  Core: <Database className="h-4 w-4" />,
  i18n: <FileText className="h-4 w-4" />,
  Performance: <Zap className="h-4 w-4" />,
  "Smart Cashier": <CreditCard className="h-4 w-4" />,
  "Device Tracking": <Shield className="h-4 w-4" />,
  Devices: <Shield className="h-4 w-4" />,
  Profile: <User className="h-4 w-4" />,
  Backend: <Database className="h-4 w-4" />,
  "Database Tools": <Database className="h-4 w-4" />,
  "Database Schema Management": <Database className="h-4 w-4" />,
  "Backend Tests": <FileText className="h-4 w-4" />,
  Notification: <Gift className="h-4 w-4" />,
};

// Current version — อัปเดตเมื่อมี release ใหม่ (หลังปรับ UI/ระบบและผ่านการทดสอบ)
const CURRENT_VERSION = "2.14.0";
const BUILD_DATE = "2026-02-04";

export function ChangelogPage() {
  const [expandedVersions, setExpandedVersions] = useState<string[]>([
    mockChangelog[0].version,
  ]);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const toggleExpand = (version: string) => {
    setExpandedVersions((prev) =>
      prev.includes(version)
        ? prev.filter((v) => v !== version)
        : [...prev, version]
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <History className="h-6 w-6 text-primary" />
            Changelog
          </h1>
          <p className="text-muted-foreground">
            ประวัติการเปลี่ยนแปลงและอัปเดตระบบ
          </p>
        </div>
        <Button className="gap-2" onClick={() => setShowAddDialog(true)}>
          <Plus className="h-4 w-4" />
          เพิ่ม Release
        </Button>
      </div>

      {/* Current Version Banner */}
      <Card className="bg-gradient-to-r from-primary/10 to-purple-500/10 border-primary/20">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/20">
                <Rocket className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  เวอร์ชันปัจจุบัน
                </p>
                <h2 className="text-3xl font-bold">v{CURRENT_VERSION}</h2>
                <p className="text-xs text-muted-foreground">
                  Build: {BUILD_DATE}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Badge className={typeConfig["minor"].color}>
                {mockChangelog[0].changes.length} การเปลี่ยนแปลง
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <div className="space-y-4">
        {mockChangelog.map((entry, index) => {
          const isExpanded = expandedVersions.includes(entry.version);
          const isLatest = index === 0;

          return (
            <Card
              key={entry.id}
              className={cn(isLatest && "border-primary/50")}
            >
              <CardHeader
                className="cursor-pointer"
                onClick={() => toggleExpand(entry.version)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={cn(
                        "p-2 rounded-lg",
                        isLatest
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      )}
                    >
                      <Tag className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        v{entry.version}
                        <Badge className={typeConfig[entry.type].color}>
                          {typeConfig[entry.type].label}
                        </Badge>
                        {isLatest && (
                          <Badge
                            variant="secondary"
                            className="bg-win/10 text-win border-0"
                          >
                            Latest
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {entry.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {entry.author}
                        </span>
                        <span>{entry.changes.length} การเปลี่ยนแปลง</span>
                      </CardDescription>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon">
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent className="pt-0">
                  <Separator className="mb-4" />
                  <div className="space-y-3">
                    {entry.changes.map((change, idx) => {
                      const config = changeTypeConfig[change.type];
                      return (
                        <div
                          key={idx}
                          className="flex items-start gap-3 p-3 rounded-lg bg-muted/30"
                        >
                          <div className={cn("mt-0.5", config.color)}>
                            {config.icon}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge
                                variant="secondary"
                                className="text-[10px] py-0 px-1.5 h-5 gap-1"
                              >
                                {moduleIcons[change.module] || (
                                  <FileText className="h-3 w-3" />
                                )}
                                {change.module}
                              </Badge>
                              <span
                                className={cn(
                                  "text-xs font-medium",
                                  config.color
                                )}
                              >
                                {config.label}
                              </span>
                            </div>
                            <p className="text-sm">{change.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Add Release Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-primary" />
              เพิ่ม Release ใหม่
            </DialogTitle>
            <DialogDescription>
              บันทึกการเปลี่ยนแปลงเวอร์ชันใหม่
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>เวอร์ชัน</Label>
                <Input placeholder="2.6.0" />
              </div>
              <div className="space-y-2">
                <Label>ประเภท</Label>
                <Select defaultValue="minor">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="major">
                      Major - เปลี่ยนแปลงใหญ่
                    </SelectItem>
                    <SelectItem value="minor">Minor - ฟีเจอร์ใหม่</SelectItem>
                    <SelectItem value="patch">Patch - แก้ไขบัก</SelectItem>
                    <SelectItem value="hotfix">Hotfix - แก้ไขด่วน</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>รายการเปลี่ยนแปลง</Label>
              <div className="p-4 rounded-lg border border-dashed border-border text-center text-sm text-muted-foreground">
                กดเพื่อเพิ่มรายการเปลี่ยนแปลง
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              ยกเลิก
            </Button>
            <Button className="gap-2">
              <Save className="h-4 w-4" />
              เผยแพร่
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
